import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import {
  TcServiceContract,
  TcServiceContractLog,
  TcServiceContractType,
} from '../../types/tc-service-contract/tc-service-contract.type';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

export const getContractsList = createAsyncThunk<
  PaginationType<TcServiceContract>,
  RouteParamsType & { contractType?: TcServiceContractType; controller?: AbortController },
  { state: RootReducerState }
>('tc-service-contracts/getList', async (data, thunkAPI) => {
  const { controller, ...params } = data;
  try {
    let additional = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    if (params.org_id) additional = {}; // NOTE: Caller-provided org_id takes precedence over the global company/location filters.
    const mergedParams = { ...params, ...additional };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<TcServiceContract>>(
      `${process.env.REACT_APP_API_URL}/tc-service-contract${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching contracts' });
  }
});

export const getContractDetails = createAsyncThunk<
  TcServiceContract,
  { id: number },
  { state: RootReducerState; rejectValue: string }
>('tc-service-contracts/getDetails', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<TcServiceContract>(`${process.env.REACT_APP_API_URL}/tc-service-contract/${id}`);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching contract details' });
  }
});

export const getContractLogs = createAsyncThunk<TcServiceContractLog[], { id: number }, { state: RootReducerState }>(
  'tc-service-contracts/getLogs',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<TcServiceContractLog[]>(
        `${process.env.REACT_APP_API_URL}/tc-service-contract/${id}/logs`
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching contract logs' });
    }
  }
);
